import { TableCell } from '@material-ui/core';

import { useRouteRef } from '@backstage/core-plugin-api';

import { LabelChips, useLabelFilters } from '@spotify/confidence-common-react';
import {
  extractLastNameComponent,
  extractWorkflowFromInstanceName,
  formatDatePast,
  useThrottledPrefetch,
} from '@spotify/confidence-core-react';
import {
  AbTestListItemFragment,
  getTypeOrNull,
} from '@spotify/confidence-graphql';
import { getDecisionLabel } from '@spotify/plugin-confidence-workflows-module-decision';
import {
  PlanningProgressCell,
  usePlanningSubtitle,
} from '@spotify/plugin-confidence-workflows-module-planning';
import {
  WorkflowInstanceTableRow,
  workflowInstanceRouteRef,
} from '@spotify/plugin-confidence-workflows-react';
import {
  NameCell,
  OwnerCell,
  StateCell,
  UpdateCell,
  useStateCell,
} from '@spotify/plugin-confidence-workflows-react';

import { useAbTestPrefetch } from '../../hooks';
import { tabForState } from '../AbTestWorkflowInstancePage/utils';

export function AbTestTableRow({
  workflowInstance,
}: {
  workflowInstance: AbTestListItemFragment;
}) {
  const workflowInstanceRoute = useRouteRef(workflowInstanceRouteRef);
  const href = workflowInstanceRoute({
    workflow: extractWorkflowFromInstanceName(workflowInstance.name) ?? '',
    id: extractLastNameComponent(workflowInstance.name) ?? '',
  });

  // Prefetching the workflow instance when hovering over the row, so that
  // when the user clicks on the row, the workflow instance is already loaded.
  const prefetch = useAbTestPrefetch();
  const { onMouseEnter, onMouseLeave } = useThrottledPrefetch(() =>
    prefetch(workflowInstance.name),
  );

  const tab = tabForState(workflowInstance);
  const stateProps = useStateCell(workflowInstance);
  const { addLabelFilter, labelDefinitions } = useLabelFilters();
  const hasLabels = labelDefinitions.length > 0;

  const planningText = usePlanningSubtitle(
    workflowInstance.moduleData2.planning,
  );

  const secondaryText =
    workflowInstance.state === 'draft'
      ? `Created ${formatDatePast(new Date(workflowInstance.createTime))}`
      : planningText;

  return (
    <WorkflowInstanceTableRow
      workflowInstance={workflowInstance}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      transform={path => `${path}/${tab}`}
    >
      <NameCell
        kind="A/B test"
        displayName={workflowInstance.displayName}
        comments={getTypeOrNull(workflowInstance.comments)}
        secondaryText={secondaryText}
        href={href}
        labels={workflowInstance.labels}
      />
      <StateCell {...stateProps} />
      <PlanningProgressCell
        workflowInstance={workflowInstance}
        outcome={getDecisionLabel(workflowInstance.moduleData2.decision)}
      />
      <UpdateCell updateTime={workflowInstance.updateTime} />
      <OwnerCell owner={getTypeOrNull(workflowInstance.owner)} />
      {hasLabels && (
        <TableCell>
          <LabelChips
            labels={workflowInstance.labels}
            labelDefinitions={labelDefinitions}
            onLabelClick={addLabelFilter}
          />
        </TableCell>
      )}
    </WorkflowInstanceTableRow>
  );
}
